import { useAtom, useSetAtom } from "jotai";
import React, { useEffect, useRef, useState } from "react";
import {
  getMouvementColorFromName,
  getGroupColorFromId,
  groupOptions,
} from "../FlotteSimulator/simulatorUtils";
import styles from "./ColumnVisibilityDropdown.module.scss";
import {
  columnsAtom,
  activeGroupsAtom,
  clearManualOverridesAtom,
} from "./useFlotteTable";
import { columnsConfig } from "./flotteColumns.config";
import { connectedUserAtom } from "../../../../../middlewares/AuthMiddleware";
import { isVORole, isVO531Role } from "./FlotteUtils";

// Groupes présentables d'une colonne : « default » est un tag technique (LS-699,
// visibilité pilotée par le rôle) et n'a ni nom ni couleur — même filtre que
// FlotteTableHeader.getGroupDecoration.
const groupesAffichables = (config) =>
  (config.groups || []).filter((g) => g !== "default");

const ColumnVisibilityDropdown = ({ visibleHeaders, setVisibleHeaders }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const [columnsState] = useAtom(columnsAtom);
  const [connectedUser] = useAtom(connectedUserAtom);
  const [activeGroups, setActiveGroups] = useAtom(activeGroupsAtom);
  const clearManualOverrides = useSetAtom(clearManualOverridesAtom);

  // Fermer le dropdown quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Gérer le changement de visibilité d'une colonne
  const handleColumnToggle = (columnKey) => {
    setVisibleHeaders((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  // Tout afficher
  const handleShowAll = () => {
    const allVisible = Object.keys(columnsState).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setVisibleHeaders(allVisible);
  };

  // Restaurer les paramètres par défaut depuis la config originale (pas l'état modifié)
  const handleRestoreDefaults = () => {
    const role = connectedUser?.role;
    const defaultVisibility = Object.keys(columnsState).reduce((acc, key) => {
      const col = columnsConfig[key];
      if (!col) {
        acc[key] = false;
        return acc;
      }
      if (isVO531Role(role)) {
        acc[key] = col.visibleVO531 ?? col.visibleVO ?? col.visible;
      } else if (isVORole(role)) {
        acc[key] = col.visibleVO ?? col.visible;
      } else {
        acc[key] = col.visible;
      }
      return acc;
    }, {});
    setVisibleHeaders(defaultVisibility);
    setActiveGroups([]);
  };

  // Gérer l'activation/désactivation d'un groupe de présélection (Contrat/BB/VO).
  // Cumulable : ne touche jamais aux autres groupes actifs.
  const handleGroupToggle = (groupId) => {
    const isActive = activeGroups.includes(groupId);

    if (isActive) {
      // Désactivation : ne touche pas aux overrides manuels, qui restent prioritaires
      setActiveGroups((prev) => prev.filter((g) => g !== groupId));
    } else {
      // Activation : réinitialise les overrides manuels du groupe pour que sa
      // visibilité dérivée reprenne la main sur toutes ses colonnes
      const groupColumnKeys = Object.keys(columnsConfig).filter((key) =>
        (columnsConfig[key].groups || []).includes(groupId),
      );
      clearManualOverrides(groupColumnKeys);
      setActiveGroups((prev) => [...prev, groupId]);
    }
  };

  // Gérer le clic sur une catégorie pour cocher/décocher toutes ses colonnes
  const handleCategoryToggle = (category, columns) => {
    // Obtenir les clés des colonnes de cette catégorie
    const columnKeys = columns.map(([key]) => key);

    // Calculer l'état actuel des colonnes de cette catégorie
    const visibleInCategory = columnKeys.filter(
      (key) => visibleHeaders[key],
    ).length;
    const totalInCategory = columnKeys.length;

    // Déterminer l'action à effectuer
    let shouldCheck;
    if (visibleInCategory === totalInCategory) {
      // Toutes sont cochées -> décocher toutes (sauf actions)
      shouldCheck = false;
    } else {
      // Pas toutes cochées -> cocher toutes
      shouldCheck = true;
    }

    // Appliquer les changements
    setVisibleHeaders((prev) => {
      const newState = { ...prev };
      columnKeys.forEach((key) => {
        // Ne jamais décocher la colonne actions
        if (key !== "actions") {
          newState[key] = shouldCheck;
        }
      });
      return newState;
    });
  };

  // Obtenir l'état d'une catégorie (toutes cochées, toutes décochées, ou mixte)
  const getCategoryState = (columns) => {
    const columnKeys = columns.map(([key]) => key);
    const visibleInCategory = columnKeys.filter(
      (key) => visibleHeaders[key],
    ).length;
    const totalInCategory = columnKeys.length;

    if (visibleInCategory === totalInCategory) return "all";
    if (visibleInCategory === 0) return "none";
    return "mixed";
  };

  // Regrouper les colonnes par catégorie et filtrer selon le terme de recherche
  const groupedColumns = React.useMemo(() => {
    const groups = {};

    Object.entries(columnsState).forEach(([key, config]) => {
      const category = config.category || "Autres";

      // Filtrer selon le terme de recherche
      const matchesSearch =
        !searchTerm ||
        config.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (config.badge &&
          config.badge.toLowerCase().includes(searchTerm.toLowerCase()));

      if (matchesSearch) {
        if (!groups[category]) {
          groups[category] = [];
        }
        groups[category].push([key, config]);
      }
    });

    return groups;
  }, [searchTerm]);

  // Répartir les catégories sur 2 colonnes en équilibrant par nombre d'items
  const [leftCategories, rightCategories] = React.useMemo(() => {
    const entries = Object.entries(groupedColumns);
    const left = [];
    const right = [];
    let leftWeight = 0;
    let rightWeight = 0;

    entries.forEach(([category, columns]) => {
      // Poids = 1 (header) + nombre de colonnes
      const weight = 1 + columns.length;
      if (leftWeight <= rightWeight) {
        left.push([category, columns]);
        leftWeight += weight;
      } else {
        right.push([category, columns]);
        rightWeight += weight;
      }
    });

    return [left, right];
  }, [groupedColumns]);

  // Compter les colonnes visibles
  const visibleCount = Object.values(visibleHeaders).filter(Boolean).length;
  const totalCount = Object.keys(columnsState).length;

  return (
    <div className={styles.columnVisibilityDropdown} ref={dropdownRef}>
      <button
        className={`btn btn-default btn-sm ${styles.toggleButton}`}
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <i className="fas fa-columns mr-2"></i>
        Colonnes ({visibleCount}/{totalCount})
        <i className={`fas fa-chevron-${isOpen ? "up" : "down"} ml-2`}></i>
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownHeader}>
            <h6 className="mb-2">Affichage des colonnes</h6>
            <div className={styles.actionButtons}>
              <button
                className="btn btn-default btn-sm p-0 text-lowercase"
                onClick={handleShowAll}
                type="button"
              >
                Tout afficher
              </button>
              {/* <button
                className="btn btn-default btn-sm p-0 text-lowercase"
                onClick={handleHideAll}
                type="button"
              >
                Tout masquer
              </button> */}
              <button
                className="btn btn-default btn-sm p-0 text-lowercase "
                onClick={handleRestoreDefaults}
                type="button"
                title="Restaurer la sélection par défaut"
              >
                <i className="fas fa-undo mr-1"></i>
                Par défaut
              </button>
              {groupOptions.map((group) => {
                const isActive = activeGroups.includes(group.id);
                return (
                  <button
                    key={group.id}
                    className={`btn btn-sm p-0 text-lowercase ${styles.groupButton} ${
                      isActive ? styles.groupButtonActive : "btn-default"
                    }`}
                    style={
                      isActive
                        ? {
                            backgroundColor: group.color,
                            borderColor: group.color,
                            color: "#fff",
                          }
                        : undefined
                    }
                    onClick={() => handleGroupToggle(group.id)}
                    type="button"
                    title={`${isActive ? "Retirer" : "Ajouter"} les colonnes du groupe ${group.name}`}
                  >
                    {group.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.searchContainer}>
            <div className="input-group input-group-sm my-3">
              <div className="input-group-prepend">
                <span className="input-group-text">
                  <i className="fas fa-search"></i>
                </span>
              </div>
              <input
                type="text"
                className="form-control"
                placeholder="Rechercher une colonne..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <div className="input-group-append">
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => setSearchTerm("")}
                    title="Effacer la recherche"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className={styles.columnList}>
            {Object.keys(groupedColumns).length > 0 ? (
              <>
                {[leftCategories, rightCategories].map(
                  (categoriesCol, colIndex) => (
                    <div key={colIndex} className={styles.columnListColumn}>
                      {categoriesCol.map(([category, columns]) => (
                        <div key={category} className={styles.categoryGroup}>
                          <div
                            className={`${styles.categoryHeader} ${styles.clickable}`}
                            onClick={() =>
                              handleCategoryToggle(category, columns)
                            }
                            title={`Cliquer pour ${
                              getCategoryState(columns) === "all"
                                ? "décocher"
                                : "cocher"
                            } toutes les colonnes de cette catégorie`}
                          >
                            <h6 className="mb-0 text-muted font-weight-bold d-flex align-items-center">
                              {/* Indicateur d'état de la catégorie */}
                              <span
                                className={`${styles.categoryIndicator} mr-2`}
                              >
                                {(() => {
                                  const state = getCategoryState(columns);
                                  if (state === "all") {
                                    return (
                                      <i className="fas fa-check-square text-success"></i>
                                    );
                                  } else if (state === "none") {
                                    return (
                                      <i className="far fa-square text-muted"></i>
                                    );
                                  } else {
                                    return (
                                      <i className="fas fa-minus-square text-warning"></i>
                                    );
                                  }
                                })()}
                              </span>

                              {/* Icône de catégorie */}
                              <i
                                className={`fas ${
                                  category === "Actions"
                                    ? "fa-cog"
                                    : category === "Identification"
                                      ? "fa-id-card"
                                      : category === "Contrat"
                                        ? "fa-file-contract"
                                        : category === "Financier"
                                          ? "fa-euro-sign"
                                          : category === "Commande"
                                            ? "fa-shopping-cart"
                                            : category === "Retour"
                                              ? "fa-undo"
                                              : category === "Vente"
                                                ? "fa-handshake"
                                                : category === "Mouvements"
                                                  ? "fa-exchange-alt"
                                                  : "fa-folder"
                                } mr-2`}
                              ></i>

                              {/* Nom et compteur */}
                              <span className="flex-grow-1">
                                {category}
                                <small className="text-muted ml-1">
                                  (
                                  {(() => {
                                    const visible = columns.filter(
                                      ([key]) => visibleHeaders[key],
                                    ).length;
                                    return `${visible}/${columns.length}`;
                                  })()}
                                  )
                                </small>
                              </span>

                              {/* Indicateur de clic */}
                              <i
                                className="fas fa-hand-pointer text-muted ml-1"
                                style={{ fontSize: "0.75rem" }}
                              ></i>
                            </h6>
                          </div>

                          <div className={styles.categoryColumns}>
                            {columns.map(([key, config]) => (
                              <div key={key} className={styles.columnItem}>
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`column-${key}`}
                                    checked={visibleHeaders[key] || false}
                                    onChange={() => handleColumnToggle(key)}
                                    disabled={key === "actions"} // Empêcher de masquer les actions
                                  />
                                  <label
                                    className={`form-check-label d-flex align-items-start ${
                                      key === "actions" ? styles.disabled : ""
                                    }`}
                                    htmlFor={`column-${key}`}
                                  >
                                    {config.badge && (
                                      <span
                                        className="badge badge-secondary mr-1 text-xs"
                                        style={{
                                          backgroundColor:
                                            getMouvementColorFromName(
                                              config.badge,
                                            ),
                                          marginTop: 2,
                                        }}
                                      >
                                        {config.badge}
                                      </span>
                                    )}
                                    {/* LS-699 (review sprint20) — « default »
                                        est un tag technique (visibilité pilotée
                                        par le rôle), pas un groupe présentable :
                                        même filtre que FlotteTableHeader, sinon
                                        pastille grise et « default » brut en
                                        infobulle. */}
                                    {groupesAffichables(config).length > 1 && (
                                        <span
                                          className="mr-1"
                                          title={`Groupes : ${groupesAffichables(
                                            config,
                                          )
                                            .map(
                                              (g) =>
                                                groupOptions.find(
                                                  (o) => o.id === g,
                                                )?.name || g,
                                            )
                                            .join(", ")}`}
                                        >
                                          {groupesAffichables(config).map((g) => (
                                            <span
                                              key={g}
                                              style={{
                                                display: "inline-block",
                                                width: 8,
                                                height: 8,
                                                borderRadius: "50%",
                                                backgroundColor:
                                                  getGroupColorFromId(g),
                                                marginRight: 2,
                                              }}
                                            />
                                          ))}
                                        </span>
                                      )}
                                    {config.title}
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ),
                )}
              </>
            ) : (
              <div className={styles.noResults}>
                <small className="text-muted">
                  <i className="fas fa-search mr-1"></i>
                  Aucune colonne trouvée pour "{searchTerm}"
                </small>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnVisibilityDropdown;
